/**
 * SETTINGS. The account hub (super-app). Groups the account-management surfaces the FRS derives from
 * the user-facing requirements, routing to the ones that exist and marking the ones still to come, so
 * the map is honest about what is wired.
 */

import { Link } from 'react-router-dom';
import type { ReactElement } from 'react';

interface Item {
  label: string;
  description: string;
  href?: string;
  soon?: boolean;
}

interface Group {
  title: string;
  items: Item[];
}

const GROUPS: Group[] = [
  {
    title: 'Account',
    items: [
      { label: 'Profile', description: 'Your details and verification tier', href: '/profile' },
      {
        label: 'Verification',
        description: 'Move up a tier to unlock more',
        href: '/verify/tier2',
      },
      {
        label: 'Business & KYB',
        description: 'Register a business and manage signatories',
        soon: true,
      },
    ],
  },
  {
    title: 'Security',
    items: [
      { label: 'PIN', description: 'Change the PIN used to authorize payments', href: '/profile' },
      { label: 'Biometrics', description: 'Use your fingerprint or face to sign in', soon: true },
      {
        label: 'Devices & sessions',
        description: 'See where you are signed in, sign out everywhere',
        href: '/profile',
      },
    ],
  },
  {
    title: 'Preferences',
    items: [
      {
        label: 'Notifications',
        description: 'Choose what reaches you, and how',
        href: '/notifications',
      },
      {
        label: 'Privacy & consent',
        description: 'What you have agreed to, and your data rights',
        href: '/disclosures',
      },
      {
        label: 'Connected accounts',
        description: 'Linked bank accounts, M-Pesa, and wallets',
        href: '/settings/accounts',
      },
    ],
  },
  {
    title: 'Data',
    items: [
      { label: 'Statements & reports', description: 'Download your activity summary', soon: true },
      {
        label: 'Data & privacy',
        description: 'View, correct, download, or delete your data (DPA)',
        href: '/settings/data',
      },
    ],
  },
];

export default function Settings() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-page-title text-ink">Settings</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Your account, security, and preferences.</p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        {GROUPS.map((g) => (
          <section key={g.title}>
            <h2 className="mb-2 px-1 text-micro font-semibold uppercase tracking-wider text-ink-subtle">
              {g.title}
            </h2>
            <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {g.items.map((it) => (
                <li key={it.label}>
                  <Row item={it} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <Link
        to="/profile"
        className="flex w-full items-center justify-center rounded-field border border-danger/30 bg-danger/5 py-2.5 text-sm font-medium text-danger transition hover:bg-danger/10"
      >
        Sign out
      </Link>
    </div>
  );
}

function Row({ item }: { item: Item }): ReactElement {
  const inner = (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{item.label}</p>
        <p className="truncate text-xs text-ink-muted">{item.description}</p>
      </div>
      {item.soon ? (
        <span className="shrink-0 rounded-pill bg-surface-sunken px-2 py-0.5 text-micro font-medium text-ink-subtle">
          Soon
        </span>
      ) : (
        <svg
          className="h-4 w-4 shrink-0 text-ink-subtle"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 6l6 6-6 6" />
        </svg>
      )}
    </div>
  );

  if (item.href && !item.soon) {
    return (
      <Link to={item.href} className="block transition hover:bg-surface-sunken">
        {inner}
      </Link>
    );
  }
  return <div className="opacity-70">{inner}</div>;
}
